/** @format */
import { Bin } from '@mpb-com/feather/components/animated-icons/bin';
import { DynamicIconHelper } from '@mpb-com/feather/components/atoms/dynamic-icon-helper';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import EditHoverSVG from '@toucan/client/global/icons/edit-hover.svg';
import EditSVG from '@toucan/client/global/icons/edit.svg';

import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';
import { Link, LinkColor } from '@toucan/client/shared/components/link';

import { styles } from './base-summary-item.styles';
import { BaseSummaryItemPrice } from './components/price';
import { BaseSummaryItemProps, StyledBaseSummaryItemProps } from './types';

export const DEFAULT_TEST_ID = 'summary-item';

export * from './components/price';
export * from './components/notification';
export * from './components/out-of-stock-notification';
export {
    type BaseSummaryItemProps,
    type StyledBaseSummaryItemProps,
} from './types';

const StyledQuoteSummaryItem = styled('li')<StyledBaseSummaryItemProps>`
    ${styles}
`;

export const BaseSummaryItem: FunctionComponent<BaseSummaryItemProps> = ({
    price,
    title,
    condition,
    image,
    sku,
    skuSuffix,
    showDelete,
    renderAs = 'li',
    disabled = false,
    onDelete,
    className = '',
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    priceElement,
    titleLink,
    onEdit,
    showEdit,
    iconSize = IconSize.L,
    children,
    topNotification,
}: BaseSummaryItemProps) => {
    const { t } = useTranslation();

    function renderTitle() {
        const elementProps = {
            className: 'title-text',
            'data-testid': 'summary-item__title',
        };

        return titleLink ? (
            <Link
                {...elementProps}
                href={titleLink.href}
                templatePath={titleLink.templatePath}
                linkColor={LinkColor.NAVIGATION}
                disabled={disabled}
            >
                {title}
            </Link>
        ) : (
            <p {...elementProps}>{title}</p>
        );
    }

    function renderSKU() {
        return (
            <p className="sku" data-testid="summary-item__sku">
                {sku}
                {skuSuffix && (
                    <span className="sku-suffix"> ({skuSuffix})</span>
                )}
            </p>
        );
    }

    function renderImage() {
        const contents = (
            <div className="image" data-testid="summary-item__image">
                {image}
                {sku && renderSKU()}
            </div>
        );

        return titleLink ? (
            <NextLink
                href={titleLink.templatePath}
                as={titleLink.href}
                legacyBehavior
            >
                {contents}
            </NextLink>
        ) : (
            contents
        );
    }

    function renderPrice() {
        if (priceElement !== undefined) {
            return priceElement;
        }

        return <BaseSummaryItemPrice price={price} />;
    }

    const priceContent = renderPrice();

    const showActionButtons = showEdit || showDelete;

    return (
        <StyledQuoteSummaryItem
            className={className}
            data-testid={dataTestId}
            as={renderAs}
            disabled={disabled}
            hasTitleLink={Boolean(titleLink)}
        >
            {topNotification}
            <div className="content">
                {image && renderImage()}
                <div className="information">
                    <div className="title-and-desc">
                        {renderTitle()}
                        {condition && (
                            <div
                                className="description"
                                data-testid="summary-item__description"
                            >
                                <FormattedCondition condition={condition} />
                            </div>
                        )}
                    </div>
                    <div className="price-and-actions">
                        {priceContent && (
                            <div
                                className="price"
                                data-testid="summary-item__price-container"
                            >
                                {priceContent}
                            </div>
                        )}
                        {showActionButtons && (
                            <div className="action-buttons">
                                {showEdit && (
                                    <Icon
                                        className="edit"
                                        data-testid="summary-item__edit-button"
                                        aria-label={t('Edit Item', {
                                            context: 'summaryItem',
                                        })}
                                        variant={IconVariant.ACTIONABLE}
                                        size={iconSize}
                                        onClick={onEdit}
                                    >
                                        <DynamicIconHelper
                                            resting={EditSVG}
                                            hover={EditHoverSVG}
                                        />
                                    </Icon>
                                )}
                                {showDelete && (
                                    <Icon
                                        className="delete"
                                        data-testid="summary-item__delete-button"
                                        aria-label={t('Delete Item', {
                                            context: 'summaryItem',
                                        })}
                                        variant={IconVariant.ACTIONABLE}
                                        size={iconSize}
                                        onClick={onDelete}
                                    >
                                        <Bin />
                                    </Icon>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {children}
        </StyledQuoteSummaryItem>
    );
};
